// @flow

import * as React from 'react'
import queryString from 'querystring'
import moment from 'moment'

// state
import { getHCMSIntegrations } from 'services/integrations'
import { getOrganisations } from 'services/organisations'
import { getFormsAppEnvironments } from 'services/forms-app-environments'

// Routing
import { useHistory, useLocation } from 'react-router-dom'

// Auth
import { signOut } from 'services/auth'
import useAuthRequest from 'hooks/useAuthRequest'

// Components
import SubmissionHistory from 'pages/submissionHistory/SubmissionHistory'

function SubmissionHistoryPage() {
  const history = useHistory()
  const location = useLocation()

  const getHCMSIntegrationsAuthenticated = useAuthRequest(getHCMSIntegrations)
  const getOrganisationsAuthenticated = useAuthRequest(getOrganisations)
  const getFormsAppEnvironmentsAuthenticated = useAuthRequest(
    getFormsAppEnvironments
  )

  const {
    formsAppEnvironmentId = null,
    formId = null,
    startDate,
    endDate,
  } = React.useMemo<{
    formsAppEnvironmentId?: number,
    formId?: number,
    startDate: string,
    endDate: string,
  }>(() => {
    if (!location.search) {
      return {}
    }
    const params = queryString.parse(location.search.substring(1))

    return {
      ...params,
      formsAppEnvironmentId: parseInt(params.formsAppEnvironmentId),
      formId: parseInt(params.formId),
      startDate:
        params.startDate ||
        moment().startOf('day').subtract(1, 'days').toISOString(),
      endDate: params.endDate || moment().toISOString(),
    }
  }, [location.search])

  const [state, setState] = React.useState<{
    fetchError: Error | null,
    isFetching: boolean,
    organisations: ExtendedOrganisation[],
  }>({
    fetchError: null,
    isFetching: true,
    organisations: [],
  })

  const updateSearch = React.useCallback(
    ({
      key,
      value,
      persist,
    }: {
      key: string,
      value: mixed,
      persist: boolean,
    }) => {
      const existing = persist
        ? queryString.parse(location.search.substring(1))
        : {}
      history.replace({
        search: queryString.stringify({
          ...existing,
          [key]: value,
        }),
      })
    },
    [history, location.search]
  )
  const selectFormsAppEnvironment = React.useCallback(
    (formsAppEnvironment: FormsAppEnvironment) => {
      updateSearch({
        key: 'formsAppEnvironmentId',
        value: formsAppEnvironment.id,
        persist: false,
      })
    },
    [updateSearch]
  )
  const selectForm = React.useCallback(
    (form: Form) => {
      updateSearch({
        key: 'formId',
        value: form.id,
        persist: true,
      })
    },
    [updateSearch]
  )
  const logout = React.useCallback(async () => {
    await signOut()
    history.replace('/login')
  }, [history])

  React.useEffect(() => {
    let ignore = false
    const fetchState = async () => {
      let newOrganisations = []
      let newFetchError = null
      try {
        const [
          formsAppEnvironments,
          organisations,
          integrations,
        ] = await Promise.all([
          getFormsAppEnvironmentsAuthenticated(),
          getOrganisationsAuthenticated(),
          getHCMSIntegrationsAuthenticated(),
        ])
        newOrganisations = organisations.reduce(
          (
            organisations: ExtendedOrganisation[],
            organisation: Organisation
          ) => {
            const hcmsIntegration = integrations.find(
              (integration) => integration.organisationId === organisation.id
            )
            if (hcmsIntegration) {
              organisations.push({
                ...organisation,
                hcmsIntegration,
                formsAppEnvironments: formsAppEnvironments.filter(
                  (formsAppEnvironment) =>
                    formsAppEnvironment.organisationId === organisation.id
                ),
              })
            }
            return organisations
          },
          []
        )
      } catch (error) {
        newFetchError = error
      }
      if (!ignore) {
        setState({
          isFetching: false,
          fetchError: newFetchError,
          organisations: newOrganisations,
        })
      }
    }
    fetchState()

    return () => {
      ignore = true
    }
  }, [
    getFormsAppEnvironmentsAuthenticated,
    getHCMSIntegrationsAuthenticated,
    getOrganisationsAuthenticated,
  ])

  React.useEffect(() => {
    if (!formsAppEnvironmentId && state.organisations[0]) {
      selectFormsAppEnvironment(state.organisations[0].formsAppEnvironments[0])
    }
  }, [formsAppEnvironmentId, selectFormsAppEnvironment, state.organisations])

  if (formsAppEnvironmentId === null) {
    return null
  }

  return (
    <SubmissionHistory
      formId={formId}
      formsAppEnvironmentId={formsAppEnvironmentId}
      loadError={state.fetchError}
      isLoading={state.isFetching}
      organisations={state.organisations}
      onSelectFormsAppEnvironment={selectFormsAppEnvironment}
      onSelectForm={selectForm}
      onLogout={logout}
      updateSearch={updateSearch}
      startDate={startDate}
      endDate={endDate}
    />
  )
}

export default React.memo<{}>(SubmissionHistoryPage)
